import type { Dayjs } from 'dayjs'
import router from 'next/router'
import React from 'react'

import Loader from '@/legacy-src/components/Loader'
import SendSessionRequestMultibooking from '@/legacy-src/components/profiles/SendSessionRequestMultibooking'
import useRebookEvent from '@/legacy-src/components/reservation/hooks/useRebookEvent'
import { Button } from '@/legacy-src/components/ui/button'
import { cn } from '@/legacy-src/lib/utils'
import { type ServiceType } from '@/legacy-src/types/GeneralStateType'
import { type TherapistType } from '@/legacy-src/types/TherapistStateType'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import { useCartResolve } from '@/src/domains/cart/hooks/useCartResolve'
import { useTherapistCart } from '@/src/domains/cart/hooks/useTherapistCart'
import { ClientBookingAvailableSlots } from '@/src/domains/client-booking/components/ClientBookingAvailableSlots'
import { ClientBookingPlannedEvents } from '@/src/domains/client-booking/components/ClientBookingPlannedEvents'
import type { FreeSlotTypeFilterWithAvailability } from '@/src/domains/client-booking/types'

import TimezoneInfo from './TimezoneInfo'
import { isAllCoveredByB2B, isAllCoveredBySubscription } from './utils'

type Props = {
  day: Dayjs | null
  therapist: TherapistType
  service: ServiceType
  showSendMessage?: boolean
  type: FreeSlotTypeFilterWithAvailability
}

const SlotsForDate: React.FC<Props> = ({ day, therapist, service, type, showSendMessage = false }) => {
  const { t } = getTranslate()
  const { selectedSlots, cartUrl } = useTherapistCart()
  const { data: resolvedCart, isLoading: isResolvedCartLoading } = useCartResolve()

  const { isRebookingInProgress } = useRebookEvent()

  if (!day || !therapist || !service) {
    return null
  }

  const allCoveredByB2B = isAllCoveredByB2B(resolvedCart?.events)
  const allCoveredBySubscription = isAllCoveredBySubscription(resolvedCart?.events)

  const proceedToCart = () => {
    router.push(cartUrl)
  }

  return (
    <div className="flex grow flex-col">
      <div className="grow">
        <TimezoneInfo />
        <div className="flex flex-col gap-4">
          <ClientBookingPlannedEvents day={day} therapist={therapist} service={service} />
          <ClientBookingAvailableSlots day={day} therapist={therapist} service={service} type={type} />
        </div>
      </div>
      <div
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 translate-y-full rounded-t-3xl bg-white px-5 pb-5 pt-4 transition-transform tablet:static tablet:z-0 tablet:mt-5 tablet:translate-y-0 tablet:bg-transparent tablet:p-0',
          selectedSlots.length > 0 && 'translate-y-0',
        )}
      >
        {selectedSlots.length > 0 && (
          <div
            className={cn(
              'flex items-center justify-between gap-2',
              selectedSlots.length > 0 ? 'opacity-100' : 'opacity-0',
            )}
          >
            <div className="text-base font-bold">
              {t('component.cart.sessionsPlural', { count: selectedSlots.length })}
            </div>
            {isResolvedCartLoading ? (
              <div>
                <Loader visible loaderType="button" />
              </div>
            ) : (
              <div className="text-base">
                {allCoveredByB2B
                  ? t('component.benefits.cart.coveredByBenefits')
                  : allCoveredBySubscription
                    ? t('component.subscription.cart.coveredBySubscription')
                    : t('general.currency', { currency: resolvedCart?.totalPrice })}
              </div>
            )}
          </div>
        )}
        {isRebookingInProgress || selectedSlots.length === 0 ? null : (
          <Button
            onClick={() => proceedToCart()}
            className="mt-2 w-full"
            size="medium"
            disabled={selectedSlots.length === 0}
          >
            {t('component.cart.continue')}
          </Button>
        )}
      </div>
      {showSendMessage && <SendSessionRequestMultibooking therapistUserId={therapist.therapistUserId} />}
    </div>
  )
}

export default SlotsForDate
